import {useMediaQuery} from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import moment from 'moment'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router'
import {useNavigate} from 'react-router-dom'
import {FavoriteButton, LikeButton} from 'entities/activity'
import {clearPair, getChatPair, PhotoTypes, useChatPairConnection} from 'entities/messages'
import {
  GalleryTypes,
  openGallery,
  setCurrentImage,
  setGalleryIsPhotoList,
  setGalleryType,
  useProfile
} from 'entities/profile'
import {clearUser, useUser} from 'entities/user'
import {countriesMap, getTooltipVerification, mediaVariants, names, skipFalsy} from 'shared/lib'
import {UserStatus} from 'shared/types'
import {Button} from 'shared/ui/buttons'
import {ArrowLeft, Dots, Hot, Logout, VerifiedWhite} from 'shared/ui/icons'
import defaultBanner from 'shared/ui/img/default-banner.jpg'
import {Tooltip} from 'shared/ui/tooltip'
import {EditAvatarButton, EditBannerButton} from 'widgets/buttons'
import {ExtraDropdown} from 'widgets/extraDropdown'
import styles from './styles.module.scss'

export const ProfileBanner: FC = () => {
  const dispatch = useDispatch()
  const {id} = useParams()
  const isClient = !!id
  const profile = useProfile()
  const user = useUser()
  const {avatarUrl, city, country, name, videoAvatarUrl, isConfirmed} = isClient ? profile : user.profile
  const age = isClient ? profile.age : moment().diff(user.profile.dateBirth, 'years')
  const is500p = useMediaQuery(`(max-width: ${styles.is500p})`)
  const {isLike, isFavorite} = useChatPairConnection()
  const [isLoading, setIsLoading] = useState(true)
  const isOnline = isClient ? profile.isOnline : true

  const bannerUrl = useMemo(() => {
    if (!profile.id && isClient) return ''

    if (!is500p) {
      const url = isClient ? profile.bannerUrl : user.profile.bannerUrl
      return url ? url + mediaVariants.banner : defaultBanner
    }

    return videoAvatarUrl || (avatarUrl ? avatarUrl + mediaVariants.square512 : defaultBanner)
  }, [
    isClient,
    profile.bannerUrl,
    user.profile.bannerUrl,
    is500p,
    videoAvatarUrl,
    avatarUrl
  ])

  const logout = () => {
    dispatch(clearUser())
    location.reload()
  }

  const isActive = useMemo(() => user.status === UserStatus.ACTIVE, [user.status])

  useEffect(() => {
    setIsLoading(true)
  }, [id])

  const HeaderButton = useCallback(
    () => {
      if (isClient) return <ButtonsForGirl idUser={id} />

      if (isActive) return <ButtonsForUser />

      if (is500p && !isActive) return (
        <Button
          styleType='outlinedGray'
          className={styles.signOutBtn}
          onClick={logout}
        >
          <Logout />
          Sign out
        </Button>
      )

      return <></>
    },
    [isClient, isActive, id, is500p]
  )

  const onClickHandler = () => {
    if (!(videoAvatarUrl || avatarUrl) || !is500p) return

    dispatch(openGallery())
    dispatch(setGalleryIsPhotoList(isClient))
    dispatch(setGalleryType(GalleryTypes.profile))
    dispatch(setCurrentImage({
      index: 0,
      src: videoAvatarUrl || avatarUrl || '',
      type: videoAvatarUrl ? PhotoTypes.VIDEO_AVATAR : PhotoTypes.avatar,
      id: videoAvatarUrl ? 'videoAvatar' : 'avatar',
    }))
  }

  return (
    <div className={styles.banner} onClick={onClickHandler}>
      <div className={styles.header}>
        <HeaderButton />
      </div>
      <div className={styles.bannerImage} >
        {isLoading ?
          <Skeleton
            classes={{root: styles.skeleton}}
            variant='rectangular'
            animation='wave'
          />
          : null}
        {bannerUrl ? <img
          className={isLoading ? styles.loading : ''}
          src={bannerUrl}
          alt={name}
          onLoad={(e) => {
            e.currentTarget.className = styles.loaded
            setIsLoading(false)
          }}
        /> : null}
      </div>
      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBtns}>
            {isClient ? <>
              <FavoriteButton value={isFavorite} idUser={id} />
              <LikeButton value={isLike} idUser={id} />
            </> : <>
              {is500p && isActive ? <EditAvatarButton /> : null}
            </>}
          </div>
          <div className={styles.footerInfo}>
            <div className={styles.footerTitle}>
              <div className={styles.name}>{name}</div>
              <div className={styles.age}>{age}</div>
              <Tooltip title={
                <div>
                  <div className={styles.tooltipHeader}>{isConfirmed ? 'Confirmed' : 'Unconfirmed'} Account</div>
                  <div className={styles.tooltipText}>{getTooltipVerification(isClient, isConfirmed)}</div>
                </div>
              }>
                <div className={names(styles.verifiedIcon, isConfirmed ? styles.active : '')}>
                  <VerifiedWhite />
                </div>
              </Tooltip>
              {profile.isPopular ? <Tooltip title={<div className={styles.tooltipHeader}>Popular member</div>}>
                <div className={styles.verifiedIcon}><Hot /></div>
              </Tooltip> : null}
            </div>
            <div className={styles.footerInfoBottom}>
              <div className={styles.location}>
                {skipFalsy(city, country ? countriesMap[country] : '').join(', ')}
              </div>
              <div className={styles.statusBadge}>
                <div className={names(styles.statusBadgeCircle, isOnline && styles.active)} />
                <p className={styles.statusBadgeText}>{isOnline ? 'Online' : 'Offline'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ButtonsForGirl = ({idUser}: {idUser: string}) => {
  const dispatch = useDispatch()
  const [isExtraDropdown, setIsExtraDropdown] = useState(false)
  const {refetch} = getChatPair(idUser, dispatch)
  const {isLike, isFavorite} = useChatPairConnection()
  const user = useProfile()
  const navigate = useNavigate()
  const is500p = useMediaQuery(`(max-width: ${styles.is500p})`)

  useEffect(() => {
    dispatch(clearPair())
    refetch().catch(console.error)
  }, [idUser, refetch])

  return (
    <>
      <div
        className={styles.backButton}
        onClick={(event) => {
          event.stopPropagation()
          navigate(-1)
        }}
      >
        <div className={styles.icon}>
          <ArrowLeft />
        </div>
        Back
      </div>
      <div className={styles.buttonsForGirl}>
        {!is500p ?
          <>
            <LikeButton value={isLike} idUser={idUser} />
            <FavoriteButton value={isFavorite} idUser={idUser} />
          </>
          : null
        }
        <div
          className={styles.iconButton}
          onClick={(event) => {
            event.stopPropagation()
            setIsExtraDropdown(true)
          }}
        >
          <Dots />
        </div>
      </div>
      <ExtraDropdown
        style={{right: 12, top: 50}}
        user={user}
        isOpen={isExtraDropdown}
        onClose={() => setIsExtraDropdown(false)}
      />
    </>
  )
}

const ButtonsForUser = () => {
  const navigate = useNavigate()
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)

  return (
    <>
      {is768p ?
        <div
          className={styles.backButton}
          onClick={(event) => {
            event.stopPropagation()
            navigate(-1)
          }}
        >
          <div className={styles.icon}>
            <ArrowLeft />
          </div>
          Back
        </div> : null
      }
      <EditBannerButton className={styles.editBanner} />
    </>
  )
}